import { Node, mergeAttributes } from "@tiptap/core";
import { NodeSelection } from "@tiptap/pm/state";
import { ReactNodeViewRenderer } from "@tiptap/react";
import { FigureNodeView } from "@/components/editor/nodeviews/FigureNodeView";
import { TIKZ_TOGGLE_SOURCE_EVENT, type TikzToggleDetail } from "@/lib/tikzFigure";

export const FigureBlock = Node.create({
  name: "figureBlock",
  group: "block",
  atom: true,

  addAttributes() {
    return {
      blockId: { default: null },
      src: { default: "" },
      caption: { default: "" },
      alt: { default: "" },
      assetId: { default: null },
      // Everything below was silently dropped: TipTap discards attributes a
      // node does not declare, so opening and saving a document destroyed a
      // figure's label, its sub-figures, its placement, alignment and width.
      // Defaults are null, not the exporter's fallbacks, so a figure that never
      // set one does not acquire it — absent keeps meaning "use the default".
      label: { default: null },
      position: { default: null },
      placement: { default: null },
      // Full width in two columns: "page" spans both (figure*), null is one column (2 Nov 2026).
      span: { default: null as string | null },
      width: { default: null },
      subfigures: { default: null },
      // TikZ figures (step 1): "tikz" with its source verbatim; absent (null)
      // is an image figure, and stays absent on save.
      kind: { default: null },
      source: { default: null },
      // A plot linked to a table (step 4a, src/lib/tikzPlot.ts): the source is
      // written from the table. Null on every other figure, and after Detach.
      plot: { default: null },
      // Any other field the block carries, kept verbatim. The defect was a
      // whitelist; this is what stops the next field going the same way.
      extra: { default: null },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="figure-block"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes(HTMLAttributes, { "data-type": "figure-block" })];
  },

  // ⌥⏎ on a selected TikZ figure flips it between the drawing and its source.
  addKeyboardShortcuts() {
    return {
      "Alt-Enter": ({ editor }) => {
        const sel = editor.state.selection;
        if (!(sel instanceof NodeSelection) || sel.node.type.name !== this.name) return false;
        if (sel.node.attrs.kind !== "tikz") return false;
        const detail: TikzToggleDetail = { pos: sel.from };
        editor.view.dom.dispatchEvent(new CustomEvent(TIKZ_TOGGLE_SOURCE_EVENT, { detail }));
        return true;
      },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(FigureNodeView);
  },
});
